import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import store from '../../store';
import setLocales from '../../store/setLocales';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import ApiUtils from '../../utils/api';
import StatsUtils from '../../utils/statistics';
import LocalStorageManager from '../../utils/localStorage/manager';
import { Api } from '../../typings/api';
import { Menu, MenuItemType } from '../../typings/menu';
import { loadPreviousCredentials, updateCredentials, setNeedCredentials } from '../Authentication/actions';
import { sendInitializationError, loadOfflineManager, initializeOfflineManager, downloadBrandThumbIfNeeded, OfflineManagerActionKeys } from '../OfflineManager/actions';
import { loadPreviousFavorites } from '../Favorites/actions';
import { AppType } from '../App/reducer';
import { loadPreviousDownloads } from '../DownloadManager/actions';
import { getAllDriveItems, getOrFetchDriveItemMeta } from '../DriveItemList/actions';
import { loadPreviousLastVisitDate, fetchNewDriveItems } from '../DriveItemList/New/actions';
import { defaultMenu } from '../MenuManager/reducer';
import { createNotification } from '../NotificationBar/actions';
import RoutesUtils from '../../utils/routes';
import AuthUtils from '../../utils/authentication';
import { ConnectionStatus, AuthenticationMode } from '../Authentication/reducer';
import { closeDownloadAllPopupIfNeeded } from '../DownloadAllPopup/actions';
import { BrandThumbConfigLocation } from '../OfflineManager/reducer';

export enum GalleryInfosActionKeys {
	REQUEST_CONFIG = '@@gallery/REQUEST_CONFIG',
	RECEIVE_CONFIG = '@@gallery/RECEIVE_CONFIG',
	REQUEST_LOCAL_TEMPLATE = '@@gallery/REQUEST_LOCAL_TEMPLATE',
	RECEIVE_LOCAL_TEMPLATE = '@@gallery/RECEIVE_LOCAL_TEMPLATE',
	REQUEST_REMOTE_TEMPLATE = '@@gallery/REQUEST_REMOTE_TEMPLATE',
	RECEIVE_REMOTE_TEMPLATE = '@@gallery/RECEIVE_REMOTE_TEMPLATE',
	FORCE_VIEW_UPDATE = '@@gallery/FORCE_VIEW_UPDATE',
	SET_FORCE_SPLASH_SCREEN = '@@gallery/SET_FORCE_SPLASH_SCREEN',
	UPDATE_TEMPLATE_BRAND = '@@gallery/UPDATE_TEMPLATE_BRAND',
	UPDATE_TEMPLATE_COMMONSTYLES = '@@gallery/UPDATE_TEMPLATE_COMMONSTYLES',
}

interface RequestConfigAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_CONFIG;
}

interface ReceiveConfigAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_CONFIG;
	data: Api.Config;
}

interface RequestLocalTemplateAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_LOCAL_TEMPLATE;
}

interface ReceiveLocalTemplateAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_LOCAL_TEMPLATE;
	data: Api.Template;
}

interface RequestRemoteTemplateAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_REMOTE_TEMPLATE;
}

interface ReceiveRemoteTemplateAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_REMOTE_TEMPLATE;
	data: Api.Template;
}

interface ForceUpdateAction extends Action {
	type: GalleryInfosActionKeys.FORCE_VIEW_UPDATE;
}

interface SetForceSplashScreen extends Action {
	type: GalleryInfosActionKeys.SET_FORCE_SPLASH_SCREEN;
	isShown: boolean;
}

interface UpdateTemplateBrandLogo extends Action {
	type: GalleryInfosActionKeys.UPDATE_TEMPLATE_BRAND;
	value: string;
}

interface UpdateTemplateCommonStyles extends Action {
	type: GalleryInfosActionKeys.UPDATE_TEMPLATE_COMMONSTYLES;
	value: Api.CommonStyles;
}

export type GalleryInfosAction = RequestConfigAction
	| ReceiveConfigAction
	| RequestLocalTemplateAction
	| ReceiveLocalTemplateAction
	| RequestRemoteTemplateAction
	| ReceiveRemoteTemplateAction
	| ForceUpdateAction
	| SetForceSplashScreen
	| UpdateTemplateBrandLogo
	| UpdateTemplateCommonStyles;

export const initializeGallery = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	const { appInfos } = getState();
	const { type } = appInfos;

	return dispatch(initIsOnline())
		.then(() => type === AppType.Single ? dispatch(fetchLocalGalleryInfos()) : {})
		.then(() => dispatch(RemoveForceSplashScreenAfterDelay()))
		.then(() => dispatch(loadPreviousCredentials()))
		.then(() => dispatch(fetchRemoteTemplate()))
		.then(() => dispatch(loadPreviousStates()))
		.then(() => dispatch(fetchStatistics()))
		.then(() => {
			dispatch(fetchNewDriveItems());

			const { galleryInfos } = getState();
			if (galleryInfos.template && galleryInfos.template.locale) {
				setLocales(store, galleryInfos.template.locale);
			}

			if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
				LocalStorageManager.setStoreGalleryInfos(galleryInfos);
			}

			if (Utils.isMaster(galleryInfos.config)) {
				return dispatch(getAllDriveItems());
			}
			return Promise.resolve({});
		})
		.then(() => dispatch(initializeOfflineManager()))
		.then(() => dispatch(fetchMenuItemsMeta()));
};

const initIsOnline = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	if (UserAgentUtils.isMobileApp) {
		return UserAgentUtils.isOnlinePromise();
	}
	return Promise.resolve({});
};

const areCredentialsNeeded = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void | Promise<void | {}> => {
	const { authentication, appInfos } = getState();

	if (appInfos.type === AppType.Multiple) {
		return Promise.resolve();
	}

	const { mode } = authentication.userInfos;
	const needSecurityCheck = !mode || AuthUtils.isNotConnected(mode);


	if (AuthUtils.needReconnecting(mode)) { // SAML on IOS need to be automatically reauthenticated
		RoutesUtils.historyPush(RoutesUtils.getPathAuthentication());
		return Promise.reject(undefined);
	}

	if (needSecurityCheck) {
		return ApiUtils.getSecurityType()
			.then(value => {
				if (value) {
					dispatch(setNeedCredentials(true, value.securityType, value.samlUrl));
					return Promise.reject(undefined);
				}
				return Promise.resolve();
			});
	}
	return Promise.resolve();
};

const fetchMenuItemsMeta = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { galleryInfos, authentication } = getState();
	const { template } = galleryInfos;
	const { needCredentials, mode } = authentication.userInfos;
	const hasAccess = !needCredentials || needCredentials && AuthUtils.isConnected(mode);

	if (template.menu && hasAccess && UserAgentUtils.isOnline()) {
		const customMenuItems = getCustomMenuItemsIds(template.menu);

		customMenuItems.forEach(item => dispatch(getOrFetchDriveItemMeta(item, false)));
	}
	return Promise.resolve({});
};

const getCustomMenuItemsIds = (menuConfig: Menu): number[] => {
	let customMenuItems: number[] = [];

	Object.keys(menuConfig).forEach(menuFormat => {
		Object.keys(menuConfig[menuFormat]).forEach(menuPosition => {
			menuConfig[menuFormat][menuPosition].forEach((menuItem, index) => {
				if (menuItem.type === MenuItemType.Asset || menuItem.type === MenuItemType.Folder || menuItem.type === MenuItemType.Page) {
					const targetId = Utils.getMediaId(menuItem.target);
					if (targetId > 0) {
						customMenuItems.push(targetId);
					}
				}

				if (menuItem.icon) {
					const iconId = Utils.getMediaId(menuItem.icon);
					if (iconId > 0) {
						customMenuItems.push(iconId);
					}
				}
			});
		});
	});

	return customMenuItems.filter((item, index, self) => index === self.indexOf(item));
};

const fetchLocalGalleryInfos = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	const { galleryInfos } = getState();
	if (galleryInfos.isConfigLoaded) {
		return Promise.resolve(undefined);
	}
	dispatch(requestConfig());
	return Utils.fetchJson(`./.gallery/config/config.json`, 'GET', undefined, true)
		.then((config: Api.Config) => {
			LocalStorageManager.setPrefix(config.applicationGallery.toString());
			dispatch(receiveConfig(config));
		})
		.then(() => dispatch(loadPreviousHomeLogo()))
		.then(() => dispatch(fetchTemplateOrLoadPreviousOne()))
		.then((template: Api.Template) => {
			if (!template.menu) {
				template.menu = defaultMenu;
			}
			dispatch(receiveLocalTemplate(template));
			setLocales(store, template.locale);

			return dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.LOCAL_CONFIG, template, true));
		});
};

const loadPreviousStates = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	return Promise.resolve(dispatch(closeDownloadAllPopupIfNeeded()))
		.then(() => dispatch(loadOfflineManager()))
		.then(() => dispatch(loadPreviousDownloads()))
		.then(() => dispatch(loadPreviousFavorites()))
		.then(() => dispatch(loadPreviousLastVisitDate()));
};

const loadPreviousHomeLogo = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const previousOfflineManagerState = LocalStorageManager.getStoreOfflineManager();

	if (previousOfflineManagerState) {
		if (previousOfflineManagerState.homeThumb) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_HOME_THUMB,
				homeThumb: previousOfflineManagerState.homeThumb,
			});
		}
	}
};

const fetchTemplateOrLoadPreviousOne = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	if (UserAgentUtils.isMobileApp && !UserAgentUtils.isOnline()) {
		const cachedTemplate = getCachedTemplate();
		if (cachedTemplate) {
			return Promise.resolve(cachedTemplate);
		}
	}
	return Utils.fetchJson(`./.gallery/config/template.json`, 'GET', undefined, true);
};

const fetchStatistics = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	if (!UserAgentUtils.isOnline()) {
		return Promise.resolve(undefined);
	}
	return ApiUtils.getStatistics()
		.then((statisticsConfig: string) => {
			StatsUtils.updateConfig(statisticsConfig);
		})
		.catch(response => {
			return Promise.resolve(undefined);
		});
};

export const fetchRemoteTemplate = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	const { galleryInfos, authentication } = getState();
	const { mode } = authentication.userInfos;

	if (UserAgentUtils.isMobileApp) {
		if (!UserAgentUtils.isOnline()) {
			const cachedTemplate = getCachedTemplate();
			if (cachedTemplate) {
				if (AuthUtils.isSaml(mode) && UserAgentUtils.isIOSApp()) {
					dispatch(updateCredentials({
						mode: { ...mode as AuthenticationMode, onCache: true }
					}));
				}
				return Promise.resolve(dispatch(receiveRemoteTemplate(cachedTemplate)));
			}

			dispatch(createNotification('info.offline_no_config', { id: galleryInfos.config.applicationGallery }, -1));
			dispatch(sendInitializationError());

			return Promise.reject(undefined);
		} else {
			return Promise.resolve(dispatch(areCredentialsNeeded()))
				.then(() => {
					const { multiAppAuthenticationInfos, appInfos } = getState();
					const server = appInfos.type === AppType.Multiple ? multiAppAuthenticationInfos.server : undefined;
					return ApiUtils.getTemplate(server)
						.then((template: Api.Template) => {
							if (AuthUtils.isSaml(mode) && (AuthUtils.isNotConnected(mode) || AuthUtils.isConnecting(mode))) {
								dispatch(updateCredentials({
									mode: { ...mode as AuthenticationMode, status: ConnectionStatus.CONNECTED }
								}));
							}

							if (!template.menu) {
								template.menu = defaultMenu;
							}

							dispatch(receiveRemoteTemplate(template));
							return dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.REMOTE_CONFIG, template, true));
						})
						.catch(err => Promise.reject(err));
				});
		}
	}

	return Promise.resolve(undefined);
};

const getCachedTemplate = (): Api.Template | null => {
	const cachedGalleryInfosState = LocalStorageManager.getStoreGalleryInfos();
	if (cachedGalleryInfosState) {
		return cachedGalleryInfosState.template;
	}
	return null;
};

const RemoveForceSplashScreenAfterDelay = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { mode } = getState().authentication.userInfos;

	if (!AuthUtils.needReconnecting(mode) || !UserAgentUtils.isOnline()) { // SAML automated reconnection on IOS is done behind the splashscreen
		setTimeout(
			() => {
				dispatch(setForceSplashscreen(false));
			},
			1500
		);
	}
	return Promise.resolve();
};

export const setForceSplashscreen = (isShown: boolean): SetForceSplashScreen => ({
	type: GalleryInfosActionKeys.SET_FORCE_SPLASH_SCREEN,
	isShown
});

export const requestConfig = (): RequestConfigAction => ({ type: GalleryInfosActionKeys.REQUEST_CONFIG });

export const receiveConfig = (data: Api.Config): ReceiveConfigAction => ({
	type: GalleryInfosActionKeys.RECEIVE_CONFIG,
	data
});

const receiveLocalTemplate = (data: Api.Template): ReceiveLocalTemplateAction => ({
	type: GalleryInfosActionKeys.RECEIVE_LOCAL_TEMPLATE,
	data
});

const receiveRemoteTemplate = (data: Api.Template): ReceiveRemoteTemplateAction => ({
	type: GalleryInfosActionKeys.RECEIVE_REMOTE_TEMPLATE,
	data
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Gallery/actions.ts